import React, { useEffect, useMemo, useRef, useState } from 'react';
import { matchesSearch } from '../utils/searchFilter';

/**
 * Dropdown with type-to-search. Options: { value: string, label: string }.
 */
export default function SearchableSelect({
  id,
  value,
  onChange,
  options,
  placeholder = 'Search…',
  emptyOption,
  disabled,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const rootRef = useRef(null);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);
  const displayLabel = selected?.label ?? (emptyOption && value === '' ? emptyOption.label : '');

  const filtered = useMemo(() => {
    const list = options.filter((o) => matchesSearch(filter, [o.label, o.value]));
    if (emptyOption && matchesSearch(filter, [emptyOption.label, ''])) {
      return [emptyOption, ...list.filter((o) => o.value !== emptyOption.value)];
    }
    return list;
  }, [options, filter, emptyOption]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setFilter('');
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) setFilter('');
  }, [open]);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setFilter('');
  };

  return (
    <div className={`mps-searchable-select ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        id={id}
        className="mps-searchable-select-trigger"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((o) => !o)}
      >
        <span className={displayLabel ? '' : 'mps-muted'}>{displayLabel || placeholder}</span>
        <span className="mps-searchable-select-chevron" aria-hidden>
          ▾
        </span>
      </button>
      {open ? (
        <div className="mps-searchable-select-panel" role="listbox">
          <input
            type="text"
            className="mps-searchable-select-input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={placeholder}
            autoComplete="off"
            autoFocus
          />
          <ul className="mps-searchable-select-list">
            {filtered.length === 0 ? (
              <li className="mps-searchable-select-empty mps-muted">No matches</li>
            ) : (
              filtered.map((o) => (
                <li key={o.value === '' ? '__empty__' : o.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={value === o.value}
                    className={`mps-searchable-select-option${value === o.value ? ' mps-searchable-select-option-active' : ''}`}
                    onClick={() => pick(o.value)}
                  >
                    {o.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
